/**
 * afterLogin — called by every login form after setAuth() succeeds.
 *
 * Responsibilities:
 *  1. Honour a validated `?redirect=/...` query param (open-redirect safe).
 *  2. Otherwise route the user to their role-based landing page.
 *  3. Surface a toast if the requested redirect was blocked.
 *
 * Back-compatibility: if /api/auth/landing ever 5xx's, we fall through
 * to a sensible default keyed on user_type so login never 'hangs'.
 */
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Used only when `/api/auth/landing` cannot answer, so login never hangs.
 *
 * iter 624 — every destination here must be a route that admits the user type
 * keyed against it, same rule as `lib/homeFor.js`. Four of these did not:
 * `/admin` is superOnly, `/admin/hospitals` is superOnly, and `/pos` and
 * `/housing` are `type="admin"` while these tokens are not. Each one sent the
 * person straight back to their own home, which sent them here again.
 * `/home` admits any signed-in person.
 *
 * `backend/tests/test_iter624_login_landing_loops.py` parses this map and
 * fails if an entry points at a route that would refuse it.
 */
const FALLBACK_BY_TYPE = {
  admin: '/home',
  teacher: (tid) => (tid ? `/school/${tid}/teacher` : '/home'),
  student: (tid) => (tid ? `/school/${tid}/student` : '/login'),
  parent: (tid) => (tid ? `/school/${tid}/parent` : '/login'),
  guardian: (tid) => (tid ? `/school/${tid}/parent` : '/login'),
  member: '/member',
  delegate: '/delegate',
  security_officer: '/security/dashboard',
  dining_staff: '/home',
  housing_staff: '/housing/staff',
  hospital_staff: '/home',
};

export function extractRedirect(search) {
  const params = new URLSearchParams(search || window.location.search);
  return params.get('redirect') || '';
}

export async function afterLogin({ navigate, userType, tenantId, redirect, token }) {
  const qs = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
  let url = null;
  let redirectHonoured = true;
  let redirectReason = 'ok';
  try {
    // Portals that keep their own token (member, security, dining, housing)
    // pass it explicitly — the shared store may not hold it yet.
    const headers = token ? { Authorization: `Bearer ${token}` } : authHeaders();
    const r = await fetch(`${API_URL}/api/auth/landing${qs}`, { headers });
    if (r.ok) {
      const data = await r.json();
      // If the resolver could not read this portal's token it answers
      // "unauthenticated" — use the local fallback rather than bounce the
      // user we have just signed in back to /login.
      if (data.reason !== 'unauthenticated') url = data.url;
      redirectHonoured = data.redirect_honoured;
      redirectReason = data.redirect_reason;
    }
  } catch {
    // network blip — fall through to local fallback
  }

  if (!url) {
    const fb = FALLBACK_BY_TYPE[userType];
    url = typeof fb === 'function' ? fb(tenantId) : (fb || '/login');
  }

  if (redirect && !redirectHonoured) {
    // Explain why we're not sending them to their requested URL.
    if (redirectReason === 'blocked_unsafe') {
      toast.error('That link is not allowed for safety reasons.');
    } else if (redirectReason === 'permission_denied') {
      toast.info("You don't have access to that page. Sending you to your workspace instead.");
    }
  }

  navigate(url, { replace: true });
}

export default afterLogin;
